import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import FlightCard2 from '../../../modules/SearchDatePicker/widgets/SearchResults/FlightCard2';
import { UrlPaxCount } from '../../../hooks/useCoreSearchParams';
import { calcFlightsTotalUtil } from '../../../utils/cartPricing';
import type { ratesInterface } from '../../../interfaces';
import { TwoOneWayResultListProps } from '../types/bot.types';
import CollapsibleResultGroup from './CollapsibleResultGroup';
import { formatBotPrice } from './formatBotPrice';
import { formatBotCurrency } from './formatBotCurrency';

type Leg = 'outbound' | 'return';

/**
 * Round trip as two separate one-way tickets (search_flights trip_mode
 * two_one_ways). Two sections, one pick each, a footer with both picks and
 * their combined total, and ONE "Add both to cart" send — the BE adds the pair
 * as a single cart item, so the widget must never send two selections.
 *
 * Rows/items are already split (`leg`) and numbered globally by the BE; the
 * indices passed to onSelectPair are those global indices.
 */
const TwoOneWayResultList: React.FC<TwoOneWayResultListProps> = ({
  results,
  items,
  disabled = false,
  onSelectPair,
}) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const rates = useSelector(
    (s: RootState) => s.config?.config?.siteConfig?.rates as ratesInterface | undefined,
  );

  // Local picks (global row indices). Reset when a new result set arrives.
  const [outPick, setOutPick] = useState<number | null>(null);
  const [retPick, setRetPick] = useState<number | null>(null);
  useEffect(() => {
    setOutPick(null);
    setRetPick(null);
  }, [results]);

  const rawOf = (i: number): any => items?.[i] ?? results[i];
  const legOf = (i: number): Leg =>
    (results[i]?.leg ?? items?.[i]?.leg) === 'return' ? 'return' : 'outbound';
  const indices = results.map((_: any, i: number) => i);
  const outboundIdx = indices.filter((i) => legOf(i) === 'outbound');
  const returnIdx = indices.filter((i) => legOf(i) === 'return');

  // Same probe SearchResultList uses: the BE stamps the searched party on each
  // sideband item so FlightCard2 can split the total into its per-pax row.
  const paxOf = (raw: any): UrlPaxCount | null => {
    const src = raw?.pax ?? raw;
    const hasPax = src?.adult != null || src?.child != null || src?.infant != null;
    if (!hasPax) return null;
    return {
      adult: Number(src.adult) || 0,
      child: Number(src.child) || 0,
      infant: Number(src.infant) || 0,
    };
  };

  const rowLabel = (i: number): string => {
    const r = results[i] ?? {};
    const name = [r.airline, r.flight_number].filter((v) => v && v !== '—').join(' ');
    return name || `#${i + 1}`;
  };

  // Combined total: site FX rates through the same helper the cart uses;
  // without rates, a plain sum when both legs share a currency.
  const total = useMemo((): { total: number; currency: string } | null => {
    if (outPick === null || retPick === null) return null;
    const legs = [rawOf(outPick), rawOf(retPick)];
    if (rates && Object.keys(rates).length) {
      // Either leg's stamp describes the same searched party, so fall through
      // to the return leg rather than defaulting to one adult: an unstamped
      // outbound would otherwise price a family of four as a single traveller
      // and show a "total for both tickets" a quarter of what the cart charges.
      const pax = paxOf(legs[0]) ??
        paxOf(legs[1]) ?? { adult: 1, child: 0, infant: 0 };
      return calcFlightsTotalUtil({
        flights: legs.map((l) => l?.flightsInfo ?? l),
        pax,
        rates,
      });
    }
    const a = results[outPick];
    const b = results[retPick];
    if (a?.currency && a.currency === b?.currency) {
      return {
        total: Math.round(Number(a.price ?? 0) + Number(b.price ?? 0)),
        currency: a.currency,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outPick, retPick, rates, results, items]);

  const renderSection = (
    title: string,
    idxs: number[],
    pick: number | null,
    setPick: (i: number) => void,
  ): React.ReactNode => (
    <CollapsibleResultGroup
      icon="✈️"
      title={title}
      count={idxs.length}
      viewOnly={disabled}
      defaultExpanded={!disabled}
    >
      <div className="bot-search-results">
        <div className="bot-search-results__list">
          {idxs.map((i) => {
            const raw = rawOf(i);
            const itemPax = paxOf(raw);
            const chosen = pick === i;
            return (
              <div
                key={i}
                className={`bot-result-card-wrapper${
                  chosen ? ' bot-result-card-wrapper--chosen' : ''
                }`}
              >
                <FlightCard2
                  flightData={raw}
                  isCompact
                  hideCabinTag
                  handlePurchase={() => {
                    if (!disabled) setPick(i);
                  }}
                  paxFromUrlParams={itemPax ?? undefined}
                  hidePaxBreakdown={!itemPax}
                />
              </div>
            );
          })}
          {idxs.length === 0 && (
            <div className="bot-two-one-way__empty">
              {t?.noFlightsThisLeg ?? 'No flights for this leg'}
            </div>
          )}
        </div>
      </div>
    </CollapsibleResultGroup>
  );

  const canAdd = !disabled && outPick !== null && retPick !== null;

  return (
    <div className="bot-two-one-way">
      <div className="bot-two-one-way__hint">
        {t?.separateTickets ?? 'Two separate one-way tickets'}
        {' · '}
        {t?.pickOneEach ?? 'Pick one outbound and one return'}
      </div>
      {renderSection(t?.outboundFlights ?? 'Outbound flights', outboundIdx, outPick, setOutPick)}
      {renderSection(t?.returnFlights ?? 'Return flights', returnIdx, retPick, setRetPick)}
      {!disabled && (
        <div className="bot-two-one-way__footer">
          <div className="bot-two-one-way__picks">
            <span>
              {t?.outboundFlights ?? 'Outbound'}:{' '}
              <strong>{outPick === null ? '—' : `#${outPick + 1} ${rowLabel(outPick)}`}</strong>
            </span>
            <span>
              {t?.returnFlights ?? 'Return'}:{' '}
              <strong>{retPick === null ? '—' : `#${retPick + 1} ${rowLabel(retPick)}`}</strong>
            </span>
          </div>
          {total && (
            <div className="bot-two-one-way__total">
              {t?.combinedTotal ?? 'Total for both tickets'}:{' '}
              <strong>
                {formatBotCurrency(total.currency)}
                {formatBotPrice(total.total)}
              </strong>
            </div>
          )}
          <button
            type="button"
            className="bot-two-one-way__add"
            disabled={!canAdd}
            onClick={() => {
              if (!canAdd || outPick === null || retPick === null) return;
              onSelectPair(outPick, retPick, `${rowLabel(outPick)} + ${rowLabel(retPick)}`);
            }}
          >
            {t?.addBothToCart ?? 'Add both to cart'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TwoOneWayResultList;
